import type { HTMLAttributes, ReactNode } from "react";

import clsx from "clsx";

import { InfoIcon } from "../../icons/info";
import { WarningIcon } from "../../icons/warning-icon";
import { Button } from "../button";

import styles from "./disclaimer.module.css";

type DisclaimerVariant = "danger" | "info" | "note" | "warning";

type DisclaimerIconProps = {
	variant: DisclaimerVariant;
};

function DisclaimerIcon({ variant }: DisclaimerIconProps): ReactNode {
	const size = variant === "note" ? 14 : 16;

	if (variant === "warning") {
		return <WarningIcon height={size} width={size} />;
	}
	return <InfoIcon height={size} width={size} />;
}

type DisclaimerProps = HTMLAttributes<HTMLDivElement> & {
	actionHref?: string;
	actionText?: string;
	className?: string;
	text: ReactNode;
	/** Omit for a "note", where the text carries the whole message. */
	title?: string;
	variant: DisclaimerVariant;
};

export function Disclaimer({
	actionHref,
	actionText,
	className,
	text,
	title,
	variant,
	...props
}: DisclaimerProps): ReactNode {
	return (
		<div className={clsx(styles.disclaimer, styles[variant], className)} {...props}>
			<div className={styles.iconWrapper}>
				<DisclaimerIcon variant={variant} />
			</div>
			<div className={styles.content}>
				{title && <h3 className="body-1 bold">{title}</h3>}
				<p className={variant === "note" ? "caption" : "body-2"}>{text}</p>
			</div>
			{actionHref && actionText && (
				<div className={styles.action}>
					<Button href={actionHref} size="sm" variant="primary">
						{actionText}
					</Button>
				</div>
			)}
		</div>
	);
}
